import React from 'react';
import PropTypes from 'prop-types';
import {FileUploadV2} from '@pepsico-ds/ui';
import {TextInput} from '@/components/atoms';
import UploadProgressBar from '@/components/atoms/UploadProgressBar';
import styles from './FreeKickGameDisplay.module.scss';
import sharedStyles from '../shared/ActivityGameDisplay.module.scss';

const TeamSection = ({
  gameData,
  updateGameField,
  handleFirstTeamImageUpload,
  handleFirstTeamImageDelete,
  handleSecondTeamImageUpload,
  handleSecondTeamImageDelete,
  firstTeamImageProgress,
  secondTeamImageProgress,
  imageValidationError,
  secondImageValidationError,
  handleFirstTeamImageFileError,
  handleSecondTeamImageFileError
}) => {
  return (
    <div className='flex flex-col gap-4'>
      <h4 className='text-sm font-bold text-white'>Teams</h4>

      {/* Team Names */}
      <div className='flex gap-4'>
        <div className={`max-w-[380px] flex-1 ${styles.teamNameWrapper}`}>
          <TextInput
            value={gameData.firstTeamName}
            onChange={(val) => updateGameField('firstTeamName', val)}
            label='First Team Name*'
            isDark
            maxLength={50}
            placeholderText='First Team Name'
            id='first-team-name'
          />
        </div>
        <div className={`max-w-[380px] flex-1 ${styles.teamNameWrapper}`}>
          <TextInput
            value={gameData.secondTeamName}
            onChange={(val) => updateGameField('secondTeamName', val)}
            label='Second Team Name*'
            isDark
            maxLength={50}
            placeholderText='Second Team Name'
            id='second-team-name'
          />
        </div>
      </div>

      {/* Team Images */}
      <div className='flex gap-4'>
        <div
          className={`max-w-[380px] flex-1 rounded-lg border border-[#4E4E4E] p-3 ${sharedStyles.imageUploadContainer}`}>
          <UploadProgressBar
            progress={firstTeamImageProgress}
            processingLabel='Processing image...'
          />
          <FileUploadV2
            accept='.jpg,.png,.webp'
            buttonLabel='Upload file'
            description='Drag and drop your file here (only .webp, .jpg,.png)'
            feedbackType={imageValidationError ? 'error' : 'info'}
            label={{
              label: 'First Team Image*',
              showLabelIcon: true,
              infoIconTooltip: {
                text: 'Max 70 KB'
              }
            }}
            type='image'
            variant={gameData.firstTeamImageUri ? 'edit' : 'default'}
            imageUri={gameData.firstTeamImageUri || undefined}
            uploadIcon='image'
            onUpdate={(files) => handleFirstTeamImageUpload(files)}
            onDelete={() => handleFirstTeamImageDelete()}
            onFileError={() => handleFirstTeamImageFileError?.()}
            data-testid='first-team-image-upload'
          />
        </div>

        <div
          className={`max-w-[380px] flex-1 rounded-lg border border-[#4E4E4E] p-3 ${sharedStyles.imageUploadContainer}`}>
          <UploadProgressBar
            progress={secondTeamImageProgress}
            processingLabel='Processing image...'
          />
          <FileUploadV2
            accept='.jpg,.png,.webp'
            buttonLabel='Upload file'
            description='Drag and drop your file here (only .webp, .jpg,.png)'
            feedbackType={secondImageValidationError ? 'error' : 'info'}
            label={{
              label: 'Second Team Image*',
              showLabelIcon: true,
              infoIconTooltip: {
                text: 'Max 70 KB'
              }
            }}
            type='image'
            variant={gameData.secondTeamImageUri ? 'edit' : 'default'}
            imageUri={gameData.secondTeamImageUri || undefined}
            uploadIcon='image'
            onUpdate={(files) => handleSecondTeamImageUpload(files)}
            onDelete={() => handleSecondTeamImageDelete()}
            onFileError={() => handleSecondTeamImageFileError?.()}
            data-testid='second-team-image-upload'
          />
        </div>
      </div>

      <div className='flex gap-4'>
        <div className='max-w-[380px] flex-1'>
          {imageValidationError ? (
            <div
              className='w-[320px] text-sm text-[color:var(--color-feedback-error-high-contrast)]'
              data-testid='team-image-validation-error'>
              {imageValidationError}
            </div>
          ) : null}
        </div>
        <div className='max-w-[380px] flex-1'>
          {secondImageValidationError ? (
            <div
              className='w-[320px] text-sm text-[color:var(--color-feedback-error-high-contrast)]'
              data-testid='second-team-image-validation-error'>
              {secondImageValidationError}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

TeamSection.propTypes = {
  gameData: PropTypes.object.isRequired,
  updateGameField: PropTypes.func.isRequired,
  handleFirstTeamImageUpload: PropTypes.func.isRequired,
  handleFirstTeamImageDelete: PropTypes.func.isRequired,
  handleSecondTeamImageUpload: PropTypes.func.isRequired,
  handleSecondTeamImageDelete: PropTypes.func.isRequired,
  firstTeamImageProgress: PropTypes.number,
  secondTeamImageProgress: PropTypes.number,
  imageValidationError: PropTypes.string,
  secondImageValidationError: PropTypes.string,
  handleFirstTeamImageFileError: PropTypes.func,
  handleSecondTeamImageFileError: PropTypes.func
};

export default React.memo(TeamSection);
